import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { Checkbox } from '../ui/checkbox';
import { FieldLabel, FieldSubLabel, FieldHelpText } from './FieldParts';
import { useElementValue, useIsReadOnly } from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';
import { Check } from '../ui/icons';

/**
 * Coerce a boolean answer to a boolean, anything else to undefined. Covers
 * the string forms a logic set_value action or an import can persist
 * ("false", "no", "off", 0) so they never read as checked.
 */
const coerceBoolean = (v: unknown): boolean | undefined => {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === 'yes' || s === 'on' || s === '1') return true;
    if (s === 'false' || s === 'no' || s === 'off' || s === '0') return false;
  }
  return undefined;
};

interface Props {
  element: DocumentElementType;
}

const CheckboxInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<boolean>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    description?: string;
    helpText?: string;
    required?: boolean;
    defaultValue?: boolean | string;
    hidden?: boolean;
  };
  const displayValue = useDefaultValue(
    coerceBoolean(value),
    setValue,
    coerceBoolean(data?.defaultValue),
  );
  const label = data?.label || 'Checkbox';
  const description = data?.description || '';
  const helpText = data?.helpText;
  const required = data?.required || false;

  // Read-only (e.g. a completed result): render the checked state as
  // static, non-interactive content — no operable checkbox.
  if (isReadOnly) {
    const checked = displayValue ?? false;
    return (
      <div className="space-y-2">
        <div className="flex items-start space-x-3">
          {checked ? (
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary bg-primary text-primary-foreground">
              <Check className="h-3 w-3" />
            </span>
          ) : (
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-input" />
          )}
          <div className="space-y-1">
            <FieldLabel htmlFor={element.id} required={required}>
              {label}
            </FieldLabel>
            <FieldSubLabel>{description}</FieldSubLabel>
            <p
              className={
                checked
                  ? 'text-sm'
                  : 'text-sm text-muted-foreground'
              }
            >
              {checked ? 'Yes' : 'No'}
            </p>
          </div>
        </div>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-start space-x-3">
        <Checkbox
          id={element.id}
          className="mt-0.5"
          checked={displayValue ?? false}
          onCheckedChange={setValue}
        />
        <div className="space-y-1">
          <FieldLabel
            htmlFor={element.id}
            required={required}
            className="cursor-pointer"
          >
            {label}
          </FieldLabel>
          <FieldSubLabel>{description}</FieldSubLabel>
        </div>
      </div>
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default CheckboxInput;
